import { Component, OnInit, Input, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import type { MaskitoOptions } from '@maskito/core';
import { LocaleService } from '../../../locale.service';
import { maskitoNumberOptionsGenerator } from '@maskito/kit';
import { maskitoIndianCurrencyOptions } from '../../../utils/maskito-masks';
import { DataObject } from '../../../interfaces/data-objects';
import { UserProfileService } from '../../../api/user-profile.service';

@Component({
  selector: 'iv-decimal-field',
  templateUrl: './decimal-field.component.html',
  styleUrls: ['./decimal-field.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DecimalFieldComponent),
      multi: true
    }
  ]
})
export class DecimalFieldComponent implements OnInit, ControlValueAccessor {

  @Input() controlName: string;
  @Input() label: string;
  @Input() disabled = false;
  @Input() required = false;
  @Input() customStyles = null;
  @Input() suffix = '';
  @Input() decimalLimit = 2;
  @Input() allowDecimal = true;
  @Input() includeThousandsSeparator = true;
  @Input() integerLimit = null;
  @Input() requireDecimal = false;
  @Input() allowNegativeValue: boolean = false;
  @Input() maxValue: number;
  @Input() isToolTipRequired: boolean = false;
  @Input() toolTipText: string = '';
  @Input() tooltipConfig: DataObject = {
    position: 'right'
  };
  @Input() formFieldClass = null;

  maskitoCurrencyOptions: MaskitoOptions;
  currencyOptions: DataObject;
  currencyMask: DataObject;
  userCountry: string;

  private innerValue: any = 0;
  onTouchedCallback: () => void = () => { };
  onChangedCallback: (_: any) => void = () => { };

  constructor(
    private userProfileService: UserProfileService,
    private localeService: LocaleService
  ) { }

  get value() {
    return this.innerValue;
  }

  set value(val: any) {
    if (val) {
      if (+val !== +this.innerValue) {
        this.innerValue = val;
        if (val.replace) {
          const inputValue = this.getNumericValue(val);
          this.onChangedCallback(inputValue);
        } else {
          this.onChangedCallback(val);
        }
      } else {
        this.onChangedCallback(val);
      }
    } else {
      // There are some logical conditions in some components where we do this check:
      // if (change.field), if we send 0, it will be treated as false, is better to send '0'
      this.onChangedCallback('0');
    }
  }

  /**
  * Write to the view when programmatic changes from model to view are requested.
  */
   writeValue(value: any): void {
    if (value !== this.innerValue) {
      this.innerValue = value;
    }
  }

  /**
   * Write form disabled state to the DOM element (model => view)
   */
  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  /**
   * Update form when DOM element value changes (view => model)
   */
  registerOnChange(fn: any): void {
    // Store the provided function as an internal method.
    this.onChangedCallback = fn;
  }

  /**
   * Update form when DOM element is blurred (view => model)
   */
  registerOnTouched(fn: any): void {
    // Store the provided function as an internal method.
    this.onTouchedCallback = fn;
  }

  // INFO: Debug purposes
  innerModelChanged(): void {
  }

  ngOnInit(): void {
    this.init();
  }

  init(): void {
    this.userCountry = this.userProfileService.getCountryLocalStorage();
    this.maskitoCurrencyOptions = this.getCurrencyOptions();
  }

  onFocus(val: any) {
    if (val === '0' || val === '0.00') {
      this.writeValue('');
    }
  }

  onBlur(val: any) {
    if (!val) {
      if (this.currencyOptions.allowDecimal) {
        this.writeValue('0.00');
      } else {
        this.writeValue('0');
      }
    }
  }

  getCurrencyOptions(): MaskitoOptions {
    let numberOptions = {};
    switch (this.userCountry) {
      default:
        numberOptions = {
          precision: (this.allowDecimal || this.decimalLimit > 0) ? this.decimalLimit : 0,
          decimalSeparator: (this.allowDecimal || this.decimalLimit > 0) ? '.' : '',
          thousandSeparator: this.includeThousandsSeparator ? ',' : '',
          minusSign: this.allowNegativeValue ? '-' : '',
        };
        break;
    }

    if (this.maxValue) {
      numberOptions = { ...numberOptions, max: this.maxValue };
    }

    this.currencyOptions = numberOptions;

    if (this.userCountry === 'IND') {
      return maskitoIndianCurrencyOptions(numberOptions);
    }

    return maskitoNumberOptionsGenerator(numberOptions);
  }

  getNumericValue(val: string): string {
    return this.localeService.getNumericValue(val, this.userCountry);
  }

}
